/**
 * Utilitaires pour traiter les recommandations PageSpeed
 */
import { isLighthouseMetricAudit, translateLighthouse } from './auditDictionary';

/** Audits Lighthouse liés à chaque métrique vitale (pour recommandations contextualisées). */
const METRIC_RELATED_AUDIT_IDS = {
  'largest-contentful-paint': [
    'largest-contentful-paint-element',
    'render-blocking-resources',
    'unused-javascript',
    'uses-responsive-images',
    'offscreen-images',
    'modern-image-formats',
    'preload-lcp-image',
  ],
  'first-contentful-paint': [
    'render-blocking-resources',
    'server-response-time',
    'uses-text-compression',
    'uses-rel-preconnect',
    'redirects',
  ],
  interactive: [
    'bootup-time',
    'mainthread-work-breakdown',
    'unused-javascript',
    'duplicated-javascript',
    'third-party-summary',
    'legacy-javascript',
  ],
  'total-blocking-time': [
    'bootup-time',
    'mainthread-work-breakdown',
    'long-tasks',
    'third-party-summary',
  ],
  'speed-index': [
    'render-blocking-resources',
    'offscreen-images',
    'unused-css-rules',
    'uses-responsive-images',
  ],
  'max-potential-fid': ['bootup-time', 'long-tasks', 'third-party-summary'],
  'interaction-to-next-paint': ['bootup-time', 'long-tasks', 'third-party-summary'],
  'experimental-interaction-to-next-paint': ['bootup-time', 'long-tasks'],
  'first-meaningful-paint': ['render-blocking-resources', 'server-response-time'],
  'cumulative-layout-shift': ['layout-shift-elements', 'cls-culprits', 'layout-shifts'],
};

function shortResourceLabel(url) {
  if (!url) return '';
  try {
    const u = new URL(url);
    const last = u.pathname.split('/').filter(Boolean).pop();
    return last || u.hostname;
  } catch {
    return String(url).split('/').pop()?.split('?')[0] || url;
  }
}

function formatBytes(n) {
  const num = Number(n);
  if (!num || num <= 0) return null;
  if (num >= 1024 * 1024) return `${(num / 1024 / 1024).toFixed(1)} Mo`;
  return `${Math.round(num / 1024)} Ko`;
}

function topResourcesFromDetails(detailsInfo, max = 3) {
  if (!detailsInfo) return [];
  const files = [...(detailsInfo.files || [])].sort(
    (a, b) => (b.wasted || b.size || 0) - (a.wasted || a.size || 0),
  );
  return files.slice(0, max);
}

function summarizeAuditForVital(auditRec) {
  const t = translateLighthouse(auditRec);
  const files = topResourcesFromDetails(auditRec.detailsInfo, 3);
  const wastedTotal = (auditRec.detailsInfo?.sizes || []).reduce(
    (sum, s) => sum + (s.wasted || 0),
    0,
  );
  const snippet = auditRec.detailsInfo?.codeSnippets?.[0];
  const selector = snippet?.selector || auditRec.detailsInfo?.selectors?.[0];
  const savings = wastedTotal > 0 ? formatBytes(wastedTotal) : null;
  const action = (t.corriger || '').trim();

  if (files.length === 1) {
    const name = files[0].name || shortResourceLabel(files[0].url);
    return `${t.label} : traiter ${name}${savings ? ` (${savings} économisables selon Google)` : ''}${action ? ` — ${action}` : ''}`;
  }
  if (files.length > 1) {
    const names = files.map((f) => f.name || shortResourceLabel(f.url)).join(', ');
    return `${t.label} : traiter en priorité ${names}${savings ? ` (${savings} économisables)` : ''}${action ? ` — ${action}` : ''}`;
  }
  if (selector) {
    return `${t.label} : corriger l'élément ${selector}${action ? ` — ${action}` : ''}`;
  }
  if (auditRec.displayValue && action) {
    return `${t.label} (${auditRec.displayValue}) — ${action}`;
  }
  if (action) return `${t.label} — ${action}`;
  return t.label;
}

/**
 * Recommandations contextualisées pour une métrique vitale, à partir des audits
 * Lighthouse en échec sur la même page (fichiers, URLs, économies réelles).
 */
export function buildVitalContextualRecommendation(metricRec, allRecommendations) {
  const related = METRIC_RELATED_AUDIT_IDS[metricRec.id] || [];
  const actions = (allRecommendations || []).filter(
    (r) => r.id !== metricRec.id && !isLighthouseMetricAudit(r.id),
  );

  const ranked = actions
    .map((r) => {
      const idx = related.indexOf(r.id);
      const relatedScore = idx >= 0 ? (related.length - idx) * 15 : 0;
      const impactScore = (r.impact ?? 0) * 100;
      return { r, score: relatedScore + impactScore };
    })
    .sort((a, b) => b.score - a.score);

  let picks = ranked.filter((x) => x.score > 5).slice(0, 3).map((x) => x.r);
  if (picks.length === 0) {
    picks = actions.slice(0, 2);
  }

  // LCP : prioriser l'audit « élément LCP » s'il existe
  if (metricRec.id === 'largest-contentful-paint') {
    const lcpEl = actions.find((r) => r.id === 'largest-contentful-paint-element');
    if (lcpEl && !picks.includes(lcpEl)) {
      picks = [lcpEl, ...picks].slice(0, 3);
    }
  }

  if (picks.length === 0) {
    const t = translateLighthouse(metricRec);
    return {
      lines: [
        t.corriger
        || 'Consulter les actions PageSpeed détaillées sur cette page pour les corrections ciblées.',
      ],
    };
  }

  return { lines: picks.map(summarizeAuditForVital) };
}

/**
 * Convertit les liens markdown en HTML
 * @param {string} text - Le texte en markdown
 * @returns {string} - Le texte avec des liens HTML
 */
export const convertMarkdownLinks = (text) => {
    if (!text) return text;
    // Remplacer [texte](url) par <a href="url" target="_blank" rel="noopener noreferrer">texte</a>
    return text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-accent hover:underline font-medium">$1</a>');
};

/**
 * Obtient la priorité basée sur le score
 * @param {number} score - Le score de 0 à 1
 * @returns {Object} - { label: string, color: string }
 */
export const getPriority = (score) => {
    if (score === null || score === undefined) return { label: 'Non disponible', color: 'secondary' };
    const scorePercent = Math.round(score * 100);
    if (scorePercent <= 30) return { label: 'Priorité haute', color: 'error' };
    if (scorePercent <= 60) return { label: 'Priorité moyenne', color: 'warning' };
    if (scorePercent <= 80) return { label: 'Priorité faible', color: 'info' };
    return { label: 'Priorité très faible', color: 'success' };
};

/** Seuils Google (en millisecondes) pour les métriques de vitesse. */
const METRIC_THRESHOLDS_MS = {
    'largest-contentful-paint': { good: 2500, poor: 4000 },
    'first-contentful-paint': { good: 1800, poor: 3000 },
    interactive: { good: 3800, poor: 7300 },
    'max-potential-fid': { good: 100, poor: 300 },
    'total-blocking-time': { good: 200, poor: 600 },
    'speed-index': { good: 3400, poor: 5800 },
    'first-meaningful-paint': { good: 2000, poor: 4000 },
    'interaction-to-next-paint': { good: 200, poor: 500 },
    'experimental-interaction-to-next-paint': { good: 200, poor: 500 },
};

/**
 * Priorité lisible pour une métrique (pas le score Lighthouse 0–1).
 */
export function getMetricPriority(auditId, numericValue) {
    if (auditId === 'cumulative-layout-shift') {
        const v = Number(numericValue);
        if (Number.isNaN(v)) return { label: 'À surveiller', color: 'warning' };
        if (v <= 0.1) return { label: 'Correct', color: 'success' };
        if (v <= 0.25) return { label: 'À améliorer', color: 'warning' };
        return { label: 'Trop instable', color: 'error' };
    }
    const th = METRIC_THRESHOLDS_MS[auditId];
    const ms = numericValue != null ? Number(numericValue) : NaN;
    if (!th || Number.isNaN(ms)) return { label: 'À surveiller', color: 'warning' };
    if (ms <= th.good) return { label: 'Correct', color: 'success' };
    if (ms <= th.poor) return { label: 'À améliorer', color: 'warning' };
    return { label: 'Trop lent', color: 'error' };
}

/**
 * Détermine si une recommandation est pour mobile, desktop ou les deux
 * @param {string} auditId - L'ID de l'audit
 * @param {Object} lighthouse - L'objet lighthouseResult
 * @returns {string} - 'mobile', 'desktop' ou 'both'
 */
export const getDeviceScope = (auditId, lighthouse) => {
    const audit = lighthouse.audits?.[auditId];
    if (!audit) return 'both';

    const title = (audit.title || '').toLowerCase();
    const description = (audit.description || '').toLowerCase();
    const id = auditId.toLowerCase();

    // Vérifier les indices dans le titre, la description ou l'ID
    const mobileIndicators = ['mobile', 'mobil', 'phone', 'smartphone', 'touch'];
    const desktopIndicators = ['desktop', 'computer', 'pc', 'laptop'];

    const hasMobileIndicator = mobileIndicators.some(ind =>
        title.includes(ind) || description.includes(ind) || id.includes(ind)
    );
    const hasDesktopIndicator = desktopIndicators.some(ind =>
        title.includes(ind) || description.includes(ind) || id.includes(ind)
    );

    if (hasMobileIndicator && !hasDesktopIndicator) return 'mobile';
    if (hasDesktopIndicator && !hasMobileIndicator) return 'desktop';

    // Par défaut, on considère que c'est pour les deux
    return 'both';
};

/**
 * Extraire les détails supplémentaires (fichiers, ressources, snippets de code, etc.)
 * @param {Object} details - L'objet details de l'audit
 * @returns {Object|null} - Les détails extraits ou null
 */
const extractDetails = (details) => {
    if (!details) return null;

    const rawItems = details.items;
    const items = Array.isArray(rawItems) ? rawItems : [];
    if (items.length === 0) return null;

    const extracted = {
        files: [],
        resources: [],
        urls: [],
        sizes: [],
        warnings: [],
        codeSnippets: [],
        selectors: [],
        headings: [],
        tableItems: []
    };

    items.forEach((item, index) => {
        // Extraire les URLs de fichiers
        if (item.url) {
            extracted.urls.push(item.url);
            // Extraire le nom du fichier
            const fileName = item.url.split('/').pop().split('?')[0];
            extracted.files.push({
                name: fileName,
                url: item.url,
                size: item.totalBytes || item.wastedBytes || item.size || null,
                type: item.mimeType || null,
                index: index
            });
        }

        // Extraire les snippets de code HTML/CSS
        if (item.node) {
            const node = item.node;
            if (node.snippet || node.nodeLabel || node.selector) {
                extracted.codeSnippets.push({
                    snippet: node.snippet || '',
                    selector: node.selector || null,
                    boundingRect: node.boundingRect || null,
                    explanation: item.explanation || null,
                    nodeLabel: node.nodeLabel || null,
                    resourceUrl: item.url || null,
                    index: index
                });
            }
            if (node.selector && !extracted.selectors.includes(node.selector)) {
                extracted.selectors.push(node.selector);
            }
        }

        // Extraire les headings (titres de tableaux)
        if (item.heading) {
            extracted.headings.push(item.heading);
        }

        // Extraire les éléments de tableau (subItems peut être un objet dans l'API PageSpeed)
        const subItems = Array.isArray(item.subItems) ? item.subItems : [];
        if (subItems.length > 0) {
            subItems.forEach(subItem => {
                if (subItem.url) {
                    const fileName = subItem.url.split('/').pop().split('?')[0];
                    extracted.files.push({
                        name: fileName,
                        url: subItem.url,
                        size: subItem.totalBytes || subItem.wastedBytes || subItem.size || null,
                        type: subItem.mimeType || null,
                        index: index
                    });
                }
                if (subItem.node && subItem.node.snippet) {
                    extracted.codeSnippets.push({
                        snippet: subItem.node.snippet,
                        selector: subItem.node.selector || null,
                        explanation: subItem.explanation || null,
                        nodeLabel: subItem.node.nodeLabel || null,
                        resourceUrl: subItem.url || null,
                        index: index
                    });
                }
            });
        }

        // Extraire les ressources
        if (item.label || item.node) {
            extracted.resources.push({
                label: item.label || item.node?.snippet || item.node?.nodeLabel || null,
                url: item.url || null,
                size: item.totalBytes || item.wastedBytes || item.size || null,
                selector: item.node?.selector || null,
                index: index
            });
        }

        // Extraire les tailles
        if (item.totalBytes || item.wastedBytes || item.size) {
            extracted.sizes.push({
                total: item.totalBytes || item.size || 0,
                wasted: item.wastedBytes || 0,
                url: item.url || null
            });
        }

        // Extraire les avertissements
        if (item.warning) {
            extracted.warnings.push(item.warning);
        }

        // Extraire les explications
        if (item.explanation && !item.node) {
            extracted.warnings.push(item.explanation);
        }
    });

    // Retourner null si aucun détail n'a été extrait
    if (extracted.files.length === 0 && extracted.codeSnippets.length === 0 &&
        extracted.resources.length === 0 && extracted.sizes.length === 0 &&
        extracted.warnings.length === 0) {
        return null;
    }

    return extracted;
};

/**
 * Indique si un audit Lighthouse doit apparaître dans les retours PageSpeed interprétés.
 * Inclut tous les audits en échec (score < 1), pas seulement le sous-ensemble « opportunity ».
 */
export function shouldIncludeLighthouseAudit(audit) {
    if (!audit?.title) return false;
    const mode = audit.scoreDisplayMode || '';
    if (mode === 'notApplicable' || mode === 'manual') return false;

    const hasScore = audit.score !== null && audit.score !== undefined;
    if (hasScore && audit.score < 1) return true;

    const hasDescription = Boolean(audit.description && String(audit.description).trim());
    const hasItems = Array.isArray(audit.details?.items) && audit.details.items.length > 0;

    if (mode === 'metricSavings') {
        return hasDescription || Boolean(audit.displayValue) || hasItems;
    }
    if (mode === 'informative' && hasItems) return true;

    return false;
}

/**
 * Extraire les recommandations détaillées depuis les données raw PageSpeed
 * @param {Object} pagespeedRaw - Données brutes PageSpeed
 * @returns {Array} - Liste des opportunités
 */
export const getRecommendations = (pagespeedRaw) => {
    if (!pagespeedRaw) {
        return [];
    }

    try {
        const lighthouse = pagespeedRaw.lighthouseResult || {};
        const audits = lighthouse.audits || {};
        const opportunities = [];

        Object.keys(audits).forEach(key => {
            const audit = audits[key];
            if (!audit) return;

            if (shouldIncludeLighthouseAudit(audit)) {
                // Calculer l'impact (plus le score est bas, plus l'impact est important)
                const impact = audit.score !== null ? (1 - audit.score) : 0;

                // Utiliser directement le titre et la description
                const title = audit.title || key;
                const description = audit.description || '';

                // Convertir les liens markdown en HTML
                const descriptionWithLinks = convertMarkdownLinks(description);

                // Déterminer le scope (mobile/desktop/both)
                const deviceScope = getDeviceScope(key, lighthouse);

                // Extraire les détails supplémentaires
                const detailsInfo = extractDetails(audit.details);

                opportunities.push({
                    id: key,
                    title: title,
                    description: descriptionWithLinks,
                    score: audit.score,
                    numericValue: audit.numericValue,
                    displayValue: audit.displayValue || '',
                    impact: impact,
                    details: audit.details,
                    detailsInfo: detailsInfo,
                    deviceScope: deviceScope
                });
            }
        });

        // Trier par impact (du plus important au moins important)
        return opportunities.sort((a, b) => (b.impact || 0) - (a.impact || 0));
    } catch (e) {
        console.error('Erreur lors de l\'extraction des recommandations:', e);
        return [];
    }
};

/**
 * Extraire les axes d'amélioration depuis les catégories PageSpeed
 * @param {Object} pagespeedRaw - Données brutes PageSpeed
 * @returns {Array} - Liste des axes d'amélioration
 */
export const getImprovementAxes = (pagespeedRaw) => {
    if (!pagespeedRaw) return [];

    try {
        const lighthouse = pagespeedRaw.lighthouseResult || {};
        const categories = lighthouse.categories || {};
        const audits = lighthouse.audits || {};
        const axes = [];

        // Parcourir les catégories pour trouver celles qui ont des opportunités
        Object.keys(categories).forEach(categoryKey => {
            const category = categories[categoryKey];
            if (!category || !category.auditRefs) return;

            // Filtrer les audits qui sont des opportunités d'amélioration
            const opportunities = category.auditRefs.filter(ref => {
                const audit = audits[ref.id];
                if (!audit) return false;

                // Les opportunités sont des audits avec :
                // - Un groupe "load-opportunities" ou "diagnostics"
                // - Un score < 1 (pas parfait)
                // - Une description (recommandation)
                const isOpportunity = (ref.group === 'load-opportunities' || ref.group === 'diagnostics') &&
                    audit.scoreDisplayMode === 'numeric' &&
                    audit.score !== null &&
                    audit.score < 1 &&
                    audit.description;

                return isOpportunity;
            });

            if (opportunities.length > 0) {
                const categoryScore = category.score !== null ? Math.round(category.score * 100) : 0;
                axes.push({
                    category: categoryKey,
                    title: category.title || categoryKey,
                    score: categoryScore,
                    opportunitiesCount: opportunities.length,
                    opportunities: opportunities.map(ref => {
                        const audit = audits[ref.id];
                        return {
                            id: ref.id,
                            title: audit?.title || ref.id,
                            description: audit?.description || '',
                            score: audit?.score,
                            displayValue: audit?.displayValue || ''
                        };
                    })
                });
            }
        });

        return axes.sort((a, b) => a.score - b.score); // Trier par score croissant (pire en premier)
    } catch (e) {
        console.error('Erreur lors de l\'extraction des axes d\'amélioration:', e);
        return [];
    }
};
